<i18n>
{
  "de": {
    "geothermalTitle": "Zulässigkeit Wärmenutzung",
    "reloadLabel": "Zulässigkeit neu abfragen",
    "pendingMessage": "Daten werden abgefragt...",
    "lastUpdateLabel": "GIS-Daten aktualisiert am",
    "ewsAdmissibility": "Erdwärmesonden",
    "gwAdmissibility": "Wärmenutzung Grundwasser",
    "thermoElementsAdmissibility": "Thermoaktive Elemente",
    "liquidRegistersAdmissibility": "Erdregister/Energiekörbe mit flüssigen Wärmeträgern",
    "airRegistersAdmissibility": "Erdregister/Energiekörbe mit Luft betrieben",
    "sourceLabel": "Quelle",
    "sourceCantonLabel": "GIS Kanton",
    "openGisBrowser": "GIS-Browser öffnen",
    "maxEwsDepthLabel": "Tiefenbeschränkung",
    "ambiguousMessage": "(Nicht eindeutig, zur Sicherheit manuell überprüfen)",
    "editLabel": "Bearbeiten",
    "saveLabel": "Speichern",
    "cancelLabel": "Abbrechen",
    "custom": "Benutzerdefiniert",
    "current": "GIS-Aktuell"
  }
}
</i18n>

<template>
  <Modal class="c-geothermal-modal" @close="$emit('close', $event)">
    <template #header>
      <h2>{{ $t('geothermalTitle') }}</h2>
    </template>
    <div v-if="error">
      <p>{{ error }}</p>
    </div>
    <div v-if="pending" class="loading">
      <ClipLoader name="c-geothermal-modal-loader" size="16px" color="#000" />
      <div>{{ $t('pendingMessage') }}</div>
    </div>
    <div v-else class="admissibilities">
      <DetailList>
        <!-- EWS view -->
        <DetailListItem v-if="!isEditing" :title="$t('ewsAdmissibility')">
          <TextWithIcon
            :icon="getIcon(admissibilities.ews, admissibilities.ews_ambiguous)"
            :text="$t(`_admissibility.${admissibilities.ews}`)"
          >
            <div v-if="admissibilities.ews_ambiguous">{{ $t('ambiguousMessage') }}</div>
            <span v-if="admissibilities.ews_max_depth">
              ({{ $t('maxEwsDepthLabel') }}: {{ admissibilities.ews_max_depth }} m)</span
            >
          </TextWithIcon>
          <template #rightContent>
            <div class="admissibility-remarks">
              {{ isEwsCustom ? $t('custom') : admissibilities.ews_remarks }}
            </div>
          </template>
        </DetailListItem>
        <!-- EWS editing -->
        <DetailListItem v-else :title="$t('ewsAdmissibility')">
          <div class="admissibility-menu">
            <AdmissibilityMenu
              :id="`ews-admissibility`"
              :selected="model.ews"
              @change="onAdmissibilityChange('ews', $event)"
            />
          </div>
          <template #rightContent>
            <div class="current-admissibility">
              {{ `${$t('current')}: ${$t(`_admissibility.${building.geothermal.admissibilities?.ews || 'UNKNOWN'}`)}` }}
            </div>
          </template>
        </DetailListItem>
        <!-- GW view -->
        <DetailListItem v-if="!isEditing" :title="$t('gwAdmissibility')">
          <TextWithIcon
            :icon="getIcon(admissibilities.gw, admissibilities.gw_ambiguous)"
            :text="$t(`_admissibility.${admissibilities.gw}`)"
          >
            <div v-if="admissibilities.gw_ambiguous">{{ $t('ambiguousMessage') }}</div>
          </TextWithIcon>
          <template #rightContent>
            <div class="admissibility-remarks">
              {{ isGwCustom ? $t('custom') : admissibilities.gw_remarks }}
            </div>
          </template>
        </DetailListItem>
        <!-- GW editing -->
        <DetailListItem v-else :title="$t('gwAdmissibility')">
          <div class="admissibility-menu">
            <AdmissibilityMenu
              :id="`gw-admissibility`"
              :selected="model.gw"
              @change="onAdmissibilityChange('gw', $event)"
            />
          </div>
          <template #rightContent>
            <div class="current-admissibility">
              {{ `${$t('current')}: ${$t(`_admissibility.${building.geothermal.admissibilities?.gw || 'UNKNOWN'}`)}` }}
            </div>
          </template>
        </DetailListItem>
        <!-- Thermo elements view -->
        <DetailListItem v-if="admissibilities.thermo_elements !== 'UNKNOWN'" :title="$t('thermoElementsAdmissibility')">
          <TextWithIcon
            :icon="getIcon(admissibilities.thermo_elements)"
            :text="$t(`_admissibility.${admissibilities.thermo_elements}`)"
          />
          <template v-if="admissibilities.thermo_elements_remarks" #rightContent>
            {{ admissibilities.thermo_elements_remarks }}
          </template>
        </DetailListItem>
        <!-- Liquid registers view -->
        <DetailListItem
          v-if="admissibilities.liquid_registers !== 'UNKNOWN'"
          :title="$t('liquidRegistersAdmissibility')"
        >
          <TextWithIcon
            :icon="getIcon(admissibilities.liquid_registers)"
            :text="$t(`_admissibility.${admissibilities.liquid_registers}`)"
          />
          <template v-if="admissibilities.liquid_registers_remarks" #rightContent>
            {{ admissibilities.liquid_registers_remarks }}
          </template>
        </DetailListItem>
        <!-- Air registers view -->
        <DetailListItem v-if="admissibilities.air_registers !== 'UNKNOWN'" :title="$t('airRegistersAdmissibility')">
          <TextWithIcon
            :icon="getIcon(admissibilities.air_registers)"
            :text="$t(`_admissibility.${admissibilities.air_registers}`)"
          />
          <template v-if="admissibilities.air_registers_remarks" #rightContent>
            {{ admissibilities.air_registers_remarks }}
          </template>
        </DetailListItem>
      </DetailList>
    </div>

    <ButtonWrapper v-if="getBuildingPermission('EDIT_GEOTHERMAL')">
      <button v-if="!isEditing" class="button" @click="onEdit">
        {{ $t('editLabel') }}
      </button>
      <button v-else class="button" :disabled="!isFormDirty" @click="onSave">
        {{ $t('saveLabel') }}
      </button>
      <button v-if="isEditing" class="button" @click="onCancel">
        {{ $t('cancelLabel') }}
      </button>
    </ButtonWrapper>

    <template v-if="!pending">
      <div class="text">
        <p v-if="building.geothermal.state === 'OK'">{{ $t('lastUpdateLabel') }}: {{ lastUpdateDate }}</p>
        <p v-if="building.geothermal.state === 'OK'">
          {{ $t('sourceLabel') }}: {{ $t('sourceCantonLabel') }} {{ building.location_info.data.canton }}
          <template v-if="building.geothermal.gis_url">
            (<a :href="building.geothermal.gis_url" target="_blank">{{ $t('openGisBrowser') }}</a
            >)
          </template>
        </p>
        <p v-if="building.geothermal.state !== 'OK'" class="missing-data">
          {{ $t(`_geothermalState.${building.geothermal.state}`) }}
        </p>
      </div>

      <ButtonWrapper v-if="getBuildingPermission('REFRESH_GEOTHERMAL')">
        <button class="button" @click="onReload">
          {{ $t('reloadLabel') }}
        </button>
      </ButtonWrapper>
    </template>
  </Modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import ClipLoader from 'vue-spinner/src/ClipLoader.vue'

import ButtonWrapper from '@/components/shared/ButtonWrapper.vue'
import Modal from '@/components/shared/Modal.vue'
import TextWithIcon from '@/components/shared/TextWithIcon.vue'
import DetailList from '@/components/shared/lists/DetailList.vue'
import DetailListItem from '@/components/shared/lists/DetailListItem.vue'
import AdmissibilityMenu from '@/components/building/feasibility/GeothermalAdmissibilityMenu.vue'

export default {
  components: {
    ButtonWrapper,
    TextWithIcon,
    DetailList,
    DetailListItem,
    Modal,
    AdmissibilityMenu,
    ClipLoader,
  },

  props: {
    building: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      pending: null,
      error: null,
      isEditing: false,
      model: {
        ews: '',
        gw: '',
      },
    }
  },

  computed: {
    ...mapGetters({
      getBuildingPermission: 'permissions/getBuildingPermission',
    }),

    lastUpdateDate() {
      return new Date(this.building.geothermal.last_update).toLocaleDateString('de-DE')
    },

    admissibilities() {
      const geothermal = this.building.geothermal
      return {
        ...geothermal.admissibilities,
        ews: geothermal.custom_admissibilities?.ews || geothermal.admissibilities?.ews || 'ERROR',
        ews_remarks: this.isEwsCustom ? '' : geothermal.admissibilities?.ews_remarks || '',
        ews_max_depth: this.isEwsCustom ? null : geothermal.admissibilities?.ews_max_depth || null,
        ews_ambiguous: this.isEwsCustom ? false : geothermal.admissibilities?.ews_ambiguous || false,
        gw: geothermal.custom_admissibilities?.gw || geothermal.admissibilities?.gw || 'ERROR',
        gw_remarks: this.isGwCustom ? '' : geothermal.admissibilities?.gw_remarks || '',
        gw_ambiguous: this.isGwCustom ? false : geothermal.admissibilities?.gw_ambiguous || false,
        thermo_elements: geothermal.admissibilities?.thermo_elements || 'ERROR',
        liquid_registers: geothermal.admissibilities?.liquid_registers || 'ERROR',
        air_registers: geothermal.admissibilities?.air_registers || 'ERROR',
      }
    },

    isEwsCustom() {
      return Boolean(this.building.geothermal.custom_admissibilities?.ews)
    },

    isGwCustom() {
      return Boolean(this.building.geothermal.custom_admissibilities?.gw)
    },

    isFormDirty() {
      const customValues = {
        ews: this.building.geothermal.custom_admissibilities?.ews || 'AUTOMATIC',
        gw: this.building.geothermal.custom_admissibilities?.gw || 'AUTOMATIC',
      }
      return this.model.ews !== customValues.ews || this.model.gw !== customValues.gw
    },
  },

  mounted() {
    this.resetModel()
  },

  methods: {
    ...mapActions({
      queryGeothermal: 'building/queryGeothermal',
      updateCustomGeothermal: 'building/updateCustomGeothermal',
    }),

    getIcon(admissibility, ambiguous = false) {
      const icons = {
        ADMISSIBLE: ambiguous ? 'alert-triangle' : 'check-circle',
        LIMITATIONS: 'alert-triangle',
        NON_ADMISSIBLE: 'x-circle',
        BAD_GEOLOGY: 'x-circle',
        ERROR: 'info-circle',
        UNKNOWN: 'help-circle',
      }
      return icons[admissibility]
    },

    onAdmissibilityChange(field, value) {
      this.model[field] = value
    },

    resetModel() {
      this.model = {
        ews: this.building.geothermal.custom_admissibilities?.ews || 'AUTOMATIC',
        gw: this.building.geothermal.custom_admissibilities?.gw || 'AUTOMATIC',
      }
    },

    onEdit() {
      this.isEditing = true
    },

    async onSave() {
      const hasCustomValues = this.model.ews !== 'AUTOMATIC' || this.model.gw !== 'AUTOMATIC'

      this.pending = true
      try {
        this.error = null
        await this.updateCustomGeothermal({
          id: this.building.id,
          // Only send custom data if not automatic
          data: hasCustomValues
            ? {
                ews: this.model.ews === 'AUTOMATIC' ? null : this.model.ews,
                gw: this.model.gw === 'AUTOMATIC' ? null : this.model.gw,
              }
            : {},
        })
      } catch (error) {
        this.error = error
      }
      this.isEditing = false
      this.pending = false
    },

    onCancel() {
      this.isEditing = false
      this.resetModel()
    },

    async onReload() {
      this.pending = true
      try {
        this.error = null
        await this.queryGeothermal({
          id: this.building.id,
        })
      } catch (error) {
        this.error = error
      }
      this.pending = false
    },
  },
}
</script>

<style lang="scss" scoped>
.missing-data {
  color: var(--warning-color);
}

.admissibilities div ~ div {
  margin-top: var(--spacing-s);
}

.text {
  margin-top: var(--spacing-m);

  & a {
    text-decoration: underline;
  }
}

.loading {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-s);
}

.admissibility-remarks {
  width: 100%;
  text-align: left;
}

.current-admissibility {
  width: 100%;
  text-align: left;
  padding-top: var(--spacing-l);
}

.admissibility-menu {
  padding-top: var(--spacing-xs);
  padding-bottom: var(--spacing-xs);
}
</style>

<style lang="scss">
.c-geothermal-modal {
  & .item-right {
    flex: 1 40px !important;
  }
}
</style>
